import { useCallback, useState } from 'react';
import { FYMAPIError } from 'fym-common/src/generics/errors/FYMAPIError';
import { InternalErrorCode } from 'fym-common/src/errors/internalErrorCodes';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { ValueOf } from 'fym-common/src/types/utilityTypes';
import { RawWidgetData } from 'fym-common/src/db/enums';
import { WizardPublishResult } from 'fym-shared/src/smartlinks/wizardPublish';
import {
  useAddUnreleasedCatalogItemMutation,
  useCreateSmartLinkMutation,
  useCreateWidgetMutation,
  useCreateWizardPresaveLinkMutation,
  useCreateWizardReleaseLinkMutation,
} from '../../../redux/api/fymAPI';
import { isSuccessfulResult, ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { ReleaseLinkData } from '../wizards/releaseLink/types';
import { CreationTemplateBlock, templateBlockData } from './blankTemplates';

export interface CreateReleasePageInput {
  main: ReleaseLinkData;
  others: ReleaseLinkData[];
  /**
   * A cover the artist already uploaded, when the page is built from a catalog
   * row that carries one. Sent instead of the cover url the links came with.
   */
  imageId?: number;
  catalogTrackMaid?: string;
  slug?: string;
  theme: SerializedSmartLinkVersionTheme;
  /**
   * The host the artist picked, sent only when it differs from the account's.
   * Left out, the page follows the account for good.
   */
  platformDomain?: string;
  /** Publish at once (the add-song flow, owner decision 6). Left out: a draft, as before. */
  publish?: boolean;
}

/** A page the sheet made. `publishResult` is set only when the call asked to publish. */
export interface CreatedSheetPage {
  smartLinkId: number;
  shareUrl: string | null;
  publishResult?: WizardPublishResult;
}

export interface CreatePresavePageInput {
  title: string;
  artistName: string;
  /** `YYYY-MM-DD`. */
  releaseDate: string;
  /** A cover we already hold. Sent instead of `imageURL`, never beside it. */
  imageId?: number;
  imageURL?: string;
  catalogTrackMaid?: string;
  catalogItemUuid?: string;
  slug?: string;
  theme: SerializedSmartLinkVersionTheme;
  /**
   * The host the artist picked, sent only when it differs from the account's.
   * Left out, the page follows the account for good.
   */
  platformDomain?: string;
  /** Set when the release has to become a catalog row first. */
  newCatalogItem?: { releaseType?: string };
  /** Publish at once (the add-song flow, owner decision 6). Left out: a draft, as before. */
  publish?: boolean;
}

export interface CreateBlankPageInput {
  name: string;
  slug: string;
  theme: SerializedSmartLinkVersionTheme;
  /**
   * The host the artist picked, sent only when it differs from the account's.
   * Left out, the page follows the account for good.
   */
  platformDomain?: string;
  blocks: CreationTemplateBlock[];
}

/** Whether a failure was the address rather than anything the artist can fix. */
function isSlugTaken(result: ErrorAPIResult): boolean {
  return (result.error as FYMAPIError).internalCode === InternalErrorCode.SmartLinkSlugIsTaken;
}

function createdPageOf(data: {
  smartLink: { id: number; shareUrl: string | null };
  publishResult?: WizardPublishResult;
}): CreatedSheetPage {
  return { smartLinkId: data.smartLink.id, shareUrl: data.smartLink.shareUrl, publishResult: data.publishResult };
}

/**
 * The three ways the sheet can end, behind one door.
 *
 * Each one lands on the same endpoint the wizards always used, so a page made
 * from the sheet is a page like any other. The only new argument is the
 * address and the theme, which the sheet asks for and the wizards never did.
 */
export function useCreateFromSheet(accountId: number) {
  const [createReleaseLink] = useCreateWizardReleaseLinkMutation();
  const [createPresaveLink] = useCreateWizardPresaveLinkMutation();
  const [createSmartLink] = useCreateSmartLinkMutation();
  const [createWidget] = useCreateWidgetMutation();
  const [addUnreleasedCatalogItem] = useAddUnreleasedCatalogItemMutation();

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<ErrorAPIResult['error']>();
  const [isSlugRejected, setIsSlugRejected] = useState(false);

  /** Common tail: remember what went wrong and give back nothing. */
  const fail = useCallback((result: ErrorAPIResult) => {
    setIsSlugRejected(isSlugTaken(result));
    setError(result.error);
    setIsCreating(false);
    return undefined;
  }, []);

  const start = useCallback(() => {
    setIsCreating(true);
    setError(undefined);
    setIsSlugRejected(false);
  }, []);

  const createReleasePage = useCallback(
    async ({
      main,
      others,
      imageId,
      catalogTrackMaid,
      slug,
      theme,
      platformDomain,
      publish,
    }: CreateReleasePageInput): Promise<CreatedSheetPage | undefined> => {
      start();
      const urls = [main, ...others].reduce<string[]>((all, link) => {
        if (link.data.url) {
          all.push(link.data.url);
        }
        return all;
      }, []);

      const result = await createReleaseLink({
        accountId,
        urls,
        title: main.data.track ?? undefined,
        subtitle: main.data.album ?? undefined,
        extra: main.data.artist ?? undefined,
        imageId,
        // A file id says everything the url would, so only one of the two is
        // ever sent. The server would ignore the url anyway.
        imageURL: imageId === undefined ? (main.data.imageUrl ?? undefined) : undefined,
        catalogTrackMaid,
        slug,
        theme,
        platformDomain,
        publish,
      });
      if (!isSuccessfulResult(result)) {
        return fail(result);
      }
      setIsCreating(false);
      return createdPageOf(result.data);
    },
    [accountId, createReleaseLink, fail, start]
  );

  const createPresavePage = useCallback(
    async (input: CreatePresavePageInput): Promise<CreatedSheetPage | undefined> => {
      start();
      let { catalogItemUuid } = input;

      // A release nothing knows about becomes a catalog row first. The Catalog
      // is where an artist's own releases live, and a campaign with no row
      // behind it would leave the two disagreeing from the day it was made.
      if (input.newCatalogItem !== undefined && catalogItemUuid === undefined) {
        const catalogResult = await addUnreleasedCatalogItem({
          accountId,
          title: input.title,
          artistName: input.artistName,
          releaseType: input.newCatalogItem.releaseType,
          releaseDate: new Date(`${input.releaseDate}T00:00:00.000Z`).toISOString(),
        });
        if (!isSuccessfulResult(catalogResult)) {
          return fail(catalogResult);
        }
        catalogItemUuid = catalogResult.data.item.uuid;
      }

      const result = await createPresaveLink({
        accountId,
        title: input.title,
        artistName: input.artistName,
        releaseDate: input.releaseDate,
        imageId: input.imageId,
        imageURL: input.imageId === undefined ? input.imageURL : undefined,
        catalogTrackMaid: input.catalogTrackMaid,
        catalogItemUuid,
        slug: input.slug,
        theme: input.theme,
        platformDomain: input.platformDomain,
        publish: input.publish,
      });
      if (!isSuccessfulResult(result)) {
        return fail(result);
      }
      setIsCreating(false);
      return createdPageOf(result.data);
    },
    [accountId, addUnreleasedCatalogItem, createPresaveLink, fail, start]
  );

  const createBlankPage = useCallback(
    async ({ name, slug, theme, platformDomain, blocks }: CreateBlankPageInput): Promise<number | undefined> => {
      start();
      const result = await createSmartLink({ accountId, name, slug, theme, platformDomain });
      if (!isSuccessfulResult(result)) {
        return fail(result);
      }
      const smartLinkId = result.data.smartLink.id;

      // In order, one at a time: position comes from what is already there, so
      // a parallel run would file them in whatever order the server answered.
      for (const block of blocks) {
        // eslint-disable-next-line no-await-in-loop
        await createWidget({
          accountId,
          smartLinkId,
          type: block.type,
          data: templateBlockData(block) as ValueOf<RawWidgetData>,
        });
      }
      setIsCreating(false);
      return smartLinkId;
    },
    [accountId, createSmartLink, createWidget, fail, start]
  );

  return { createReleasePage, createPresavePage, createBlankPage, isCreating, error, isSlugRejected };
}
